import React, { useRef, useEffect, useState } from 'react';
import StepNavigation, { NavigationButtons } from '../navigation/StepNavigation';
import { useDecisionVoucherContext } from '../../context';

// Fonction auxiliaire pour détecter les panneaux étroits
const isNarrowPanel = (element: HTMLElement | null): boolean => {
  if (!element) return false;
  return element.offsetWidth < 180;
};

const LeftColumn: React.FC = () => {
  const { saveVoucher, isDirty, isSaving } = useDecisionVoucherContext();
  const [isCompact, setIsCompact] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Détection des redimensionnements
  useEffect(() => {
    const checkWidth = () => {
      setIsCompact(isNarrowPanel(containerRef.current));
    };

    // Vérifier la largeur initiale
    checkWidth();

    // Observer les changements de taille
    const observer = new ResizeObserver(checkWidth);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  return (
    <div ref={containerRef} className="tw-flex tw-flex-col tw-h-full tw-relative">
      {/* Section scrollable */}
      <div className="tw-flex-grow tw-overflow-y-auto tw-pb-24">
        {/* Navigation entre étapes */}
        <div className="tw-p-3">
          <StepNavigation />
        </div>

        {/* Divider */}
        <div className="tw-border-t tw-border-gray-200"></div>

        {/* Auto-save indicator */}
        <div className="tw-p-3">
          <div className="tw-flex tw-items-center tw-justify-between">
            <span className={`tw-text-sm tw-text-gray-500 ${isCompact ? 'tw-hidden' : ''}`}>Statut</span>
            <div className="tw-flex tw-items-center">
              {isSaving ? (
                <>
                  <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-yellow-500 tw-mr-2 tw-animate-pulse"></div>
                  <span className={`tw-text-xs tw-text-gray-600 ${isCompact ? 'tw-hidden' : ''}`}>Sauvegarde...</span>
                </>
              ) : isDirty ? (
                <>
                  <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-orange-500 tw-mr-2"></div>
                  <span className={`tw-text-xs tw-text-gray-600 ${isCompact ? 'tw-hidden' : ''}`}>Non sauvegardé</span>
                </>
              ) : (
                <>
                  <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-green-500 tw-mr-2"></div>
                  <span className={`tw-text-xs tw-text-gray-600 ${isCompact ? 'tw-hidden' : ''}`}>Sauvegardé</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Section sticky en bas */}
      <div className="tw-w-full tw-absolute tw-bottom-0 tw-left-0 tw-bg-white tw-z-10 tw-shadow-md">
        {/* Boutons de navigation précédent/suivant */}
        <div className="tw-p-3 tw-border-t tw-border-gray-200">
          <NavigationButtons />
        </div>

        {/* Séparateur */}
        <div className="tw-border-t tw-border-gray-100"></div>

        {/* Save button */}
        <div className="tw-p-3">
          {isCompact ? (
            // Version compacte du bouton de sauvegarde
            <button
              onClick={() => saveVoucher()}
              disabled={!isDirty || isSaving}
              className={`tw-w-full tw-py-2 tw-flex tw-justify-center tw-items-center tw-rounded tw-text-sm tw-font-medium tw-transition-colors tw-focus:outline-none
                ${isDirty && !isSaving
                  ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-focus:bg-blue-600'
                  : 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
                }`}
              title={isSaving ? 'Sauvegarde en cours...' : 'Sauvegarder manuellement'}
            >
              <i className={`fas ${isSaving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
            </button>
          ) : (
            // Version normale du bouton de sauvegarde
            <button
              onClick={() => saveVoucher()}
              disabled={!isDirty || isSaving}
              className={`tw-w-full tw-py-2 tw-px-4 tw-rounded tw-text-sm tw-font-medium tw-transition-colors tw-focus:outline-none
                ${isDirty && !isSaving
                  ? 'tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-focus:bg-blue-600'
                  : 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
                }`}
            >
              {isSaving ? 'Sauvegarde en cours...' : 'Sauvegarder manuellement'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeftColumn;